import { useState, useRef, useEffect } from 'react';
import { X, Send, BotMessageSquare, AlertTriangle, ClipboardList, ThumbsUp, ThumbsDown } from 'lucide-react';
import { dashboardService } from '@/services/dashboardService';
import { useDashboardStore } from '@/stores/dashboardStore';
import { useDashboard } from '@/hooks/useDashboard';
import type { ChatMessage, ChatAction, ChatEntity, ChatSuggestion, DailyReport, UserRole } from '@/types/dashboard';

const TAB_QUICK_QUESTIONS: Record<string, string[]> = {
  综合驾驶舱: ['今日出库收入是多少', '库存健康分布怎么看', '毛利率是什么意思'],
  每日出库: ['今日出库收入是多少', '省办达成率排名', '今日未出库客户有哪些'],
  进销存: ['库存周转天数最高的省办', '可销天数是什么意思', '期末库存箱数是多少'],
  三一工程: ['三一门店数是多少', '万元达标率是什么意思', '执行反馈明细在哪看'],
  车铺车销: ['车铺成交率是多少', '车铺成交金额排名', '车铺任务记录在哪看'],
  校园压仓: ['压仓总销额是多少', '学校压仓达成率排名', '省办压仓排名'],
  团队执行力: ['团队执行力得分是多少', '人员得分排名', '执行力是什么意思'],
  客户综合评估: ['高风险客户有哪些', '客户综合风险得分是什么意思', '客户价值健康矩阵怎么看'],
  异常定位雷达: ['省办异常排名', '人员异常排名', '异常雷达是什么意思'],
};

const ROLE_QUICK_QUESTIONS: Record<UserRole, string[]> = {
  sales_executive: ['今日整体达成率是多少', '战区排名怎么样', '高风险客户有哪些', '库存健康分布怎么看'],
  region_manager: ['本省办达成率排名', '车铺车销成交率是多少', '三一工程门店数', '异常省办有哪些'],
  office_manager: ['本省达成率是多少', '本省库存周转天数', '客户覆盖情况', '人员执行力排名'],
  sales_rep: ['今日出库任务', '我的客户列表', '今日异常处理', '客户拜访记录'],
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: '你好，我是看板智能助手。可以帮你找页面、查数据、解释指标。',
      status: 'done',
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [feedbackSentIds, setFeedbackSentIds] = useState<Set<string>>(new Set());
  const [userRole, setUserRole] = useState<UserRole>('sales_executive');
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const roleDropdownRef = useRef<HTMLDivElement>(null);
  const streamingIdxRef = useRef<number | null>(null);
  const typingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  /** 已收到的完整正文，用于计算每次 SSE 推送的增量 */
  const fullTextRef = useRef('');
  /** 等待打字机输出的字符队列 */
  const typingQueueRef = useRef('');
  /** SSE 已结束，队列消费完即可标记 done */
  const pendingDoneRef = useRef(false);
  /** 流式结束时需要一并写入消息的元数据 */
  const finalActionRef = useRef<ChatAction | undefined>(undefined);
  const finalSuggestionsRef = useRef<ChatSuggestion[] | undefined>(undefined);
  const finalReportRef = useRef<DailyReport | undefined>(undefined);
  const finalMessageIdRef = useRef<string | undefined>(undefined);
  const { currentTab, filters } = useDashboardStore();
  const { applyChatAction } = useDashboard();

  // 助手打开时刷新异常数量 badge
  useEffect(() => {
    if (!open) return;
    void dashboardService.fetchAlerts().then((res) => setAlertCount(res.count));
  }, [open]);

  // 清理打字机定时器
  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
    { value: 'sales_executive', label: '销售总' },
    { value: 'region_manager', label: '战区经理' },
    { value: 'office_manager', label: '省办主任' },
    { value: 'sales_rep', label: '业务员' },
  ];

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  // 角色下拉点击外部关闭
  useEffect(() => {
    if (!roleDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(e.target as Node)) {
        setRoleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [roleDropdownOpen]);

  /** 把最终 action / suggestions / messageId 写入当前流式消息 */
  const finishStreamingMessage = () => {
    updateStreamingMessage(fullTextRef.current, {
      status: 'done',
      ...(finalActionRef.current ? { action: finalActionRef.current } : {}),
      ...(finalSuggestionsRef.current && finalSuggestionsRef.current.length > 0
        ? { suggestions: finalSuggestionsRef.current }
        : {}),
      ...(finalReportRef.current ? { action: { type: 'show_daily_report', report: finalReportRef.current } } : {}),
      ...(finalMessageIdRef.current ? { messageId: finalMessageIdRef.current } : {}),
    });
  };

  /** 确保打字机定时器在运行；基于字符队列消费，支持 SSE 多次追加 */
  const ensureTyping = (idx: number, speedMs = 18) => {
    if (typingTimerRef.current) return;

    setMessages((prev) => {
      const next = [...prev];
      if (next[idx]) {
        next[idx] = { ...next[idx], displayContent: '', status: 'typing' };
      }
      return next;
    });

    typingTimerRef.current = setInterval(() => {
      const queue = typingQueueRef.current;
      if (queue.length === 0) {
        if (pendingDoneRef.current) {
          if (typingTimerRef.current) {
            clearInterval(typingTimerRef.current);
            typingTimerRef.current = null;
          }
          finishStreamingMessage();
        }
        return;
      }

      const char = queue[0];
      typingQueueRef.current = queue.slice(1);
      setMessages((prev) => {
        const next = [...prev];
        if (next[idx]) {
          next[idx] = {
            ...next[idx],
            displayContent: (next[idx].displayContent || '') + char,
            content: fullTextRef.current,
          };
        }
        return next;
      });
    }, speedMs);
  };

  /** 更新流式消息的状态/内容/动作/建议 */
  const updateStreamingMessage = (
    content?: string,
    updates?: Partial<ChatMessage>,
  ) => {
    setMessages((prev) => {
      const idx = streamingIdxRef.current;
      if (idx == null || idx >= prev.length) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], ...(content !== undefined ? { content } : {}), ...updates };
      return next;
    });
  };

  /** 追加一条思考步骤 */
  const appendThinkingStep = (step: string) => {
    setMessages((prev) => {
      const idx = streamingIdxRef.current;
      if (idx == null || idx >= prev.length) return prev;
      const next = [...prev];
      const current = next[idx];
      const steps = current.thinkingSteps ? [...current.thinkingSteps, step] : [step];
      next[idx] = { ...current, thinkingSteps: steps };
      return next;
    });
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    // 取消上一个未完成的请求
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
    }

    // 重置流式状态
    fullTextRef.current = '';
    typingQueueRef.current = '';
    pendingDoneRef.current = false;

    const userMsg: ChatMessage = { role: 'user', content: text };
    const assistantIdx = messages.length + 1;

    setMessages((prev) => [
      ...prev,
      userMsg,
      {
        role: 'assistant',
        content: '正在理解您的问题…',
        status: 'thinking',
        displayContent: '',
      },
    ]);
    streamingIdxRef.current = assistantIdx;
    setInput('');
    setLoading(true);

    // 前端兜底超时：50 秒无响应则中断并提示
    abortRef.current = new AbortController();
    const timeoutTimer = setTimeout(() => {
      abortRef.current?.abort();
    }, 50000);

    let receivedContent = false;
    finalActionRef.current = undefined;
    finalSuggestionsRef.current = undefined;
    finalReportRef.current = undefined;
    finalMessageIdRef.current = undefined;

    try {
      await dashboardService.sendChatMessage(
        text,
        { tabId: currentTab, filters, userRole },
        messages,
        (event) => {
          if (event.type === 'thinking') {
            const step = event.text || '正在理解您的问题…';
            updateStreamingMessage(step);
            if (event.steps && event.steps.length > 0) {
              event.steps.forEach((s) => appendThinkingStep(s));
            } else {
              appendThinkingStep(step);
            }
          } else if (event.type === 'content') {
            const newText = event.text || '';
            const delta = newText.slice(fullTextRef.current.length);
            if (delta) {
              typingQueueRef.current += delta;
            }
            fullTextRef.current = newText;
            if (!receivedContent) {
              receivedContent = true;
            }
            ensureTyping(streamingIdxRef.current!);
          } else if (event.type === 'action' && event.action) {
            finalActionRef.current = event.action;
          } else if (event.type === 'suggestions' && event.suggestions) {
            finalSuggestionsRef.current = event.suggestions;
          } else if (event.type === 'daily_report' && event.report) {
            finalReportRef.current = event.report;
            finalSuggestionsRef.current = [
              { label: '查看省办达成率', question: '省办达成率最低的5个省办' },
              { label: '查看库存风险', question: '库存周转天数最高的前5个省办' },
              { label: '查看高风险客户', question: '高风险客户有哪些' },
            ];
          } else if (event.type === 'error') {
            updateStreamingMessage(event.error || '请求失败，请稍后重试。', {
              status: 'error',
            });
            setLoading(false);
            streamingIdxRef.current = null;
            clearTimeout(timeoutTimer);
          } else if (event.type === 'done') {
            finalMessageIdRef.current = event.messageId;
            clearTimeout(timeoutTimer);
            pendingDoneRef.current = true;
            // 如果队列已空，直接完成
            if (typingQueueRef.current.length === 0) {
              finishStreamingMessage();
            }
          }
        },
        abortRef.current.signal,
      );

      // 流正常结束：done 事件已设置 pendingDoneRef，由 ensureTyping 在队列消费完后调用 finishStreamingMessage。
      // 兜底：未收到正文但拿到日报的场景（如直接生成日报）。
      if (!receivedContent && finalReportRef.current) {
        const report = finalReportRef.current as DailyReport;
        updateStreamingMessage(report.summary, {
          status: 'done',
          action: { type: 'show_daily_report', report },
          ...(finalSuggestionsRef.current ? { suggestions: finalSuggestionsRef.current } : {}),
          ...(finalMessageIdRef.current ? { messageId: finalMessageIdRef.current } : {}),
        });
      }
    } catch (err) {
      const isAbort = err instanceof Error && err.name === 'AbortError';
      updateStreamingMessage(
        isAbort ? '请求超时，助手响应较慢，请稍后重试。' : err instanceof Error ? err.message : '请求失败，请稍后重试。',
        { status: 'error' },
      );
    } finally {
      setLoading(false);
      streamingIdxRef.current = null;
      abortRef.current = null;
      clearTimeout(timeoutTimer);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  const handleActionClick = (action: ChatAction) => {
    void applyChatAction(action);
    setOpen(false);
  };

  const handleEntityClick = (entity: ChatEntity, fallbackTabId?: string) => {
    void applyChatAction({ type: 'drill_down', tabId: fallbackTabId || currentTab, dim: entity.dim, value: entity.value });
    setOpen(false);
  };

  const handleSuggestionClick = (suggestion: ChatSuggestion) => {
    if (suggestion.action) {
      void applyChatAction(suggestion.action);
      setOpen(false);
    } else {
      setInput(suggestion.question);
      setTimeout(() => handleSend(), 0);
    }
  };

  const handleGenerateReport = async () => {
    if (loading) return;

    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
    }

    const userMsg: ChatMessage = { role: 'user', content: '生成今日日报' };
    const assistantIdx = messages.length + 1;
    setMessages((prev) => [
      ...prev,
      userMsg,
      { role: 'assistant', content: '正在汇总经营日报…', status: 'thinking', displayContent: '' },
    ]);
    streamingIdxRef.current = assistantIdx;
    setLoading(true);

    abortRef.current = new AbortController();
    const timeoutTimer = setTimeout(() => abortRef.current?.abort(), 50000);

    try {
      await dashboardService.generateDailyReport((event) => {
        if (event.type === 'thinking') {
          const step = event.text || '正在汇总经营日报…';
          updateStreamingMessage(step);
          if (event.steps && event.steps.length > 0) {
            event.steps.forEach((s) => appendThinkingStep(s));
          } else {
            appendThinkingStep(step);
          }
        } else if (event.type === 'daily_report' && event.report) {
          updateStreamingMessage(event.report.summary, {
            status: 'done',
            action: { type: 'show_daily_report', report: event.report },
            suggestions: [
              { label: '查看省办达成率', question: '省办达成率最低的5个省办' },
              { label: '查看库存风险', question: '库存周转天数最高的前5个省办' },
              { label: '查看高风险客户', question: '高风险客户有哪些' },
            ],
          });
          clearTimeout(timeoutTimer);
        } else if (event.type === 'error') {
          updateStreamingMessage(event.error || '日报生成失败，请稍后重试。', { status: 'error' });
          clearTimeout(timeoutTimer);
        }
      }, abortRef.current.signal);
    } catch (err) {
      const isAbort = err instanceof Error && err.name === 'AbortError';
      updateStreamingMessage(
        isAbort ? '请求超时，请稍后重试。' : err instanceof Error ? err.message : '日报生成失败，请稍后重试。',
        { status: 'error' },
      );
    } finally {
      setLoading(false);
      streamingIdxRef.current = null;
      abortRef.current = null;
      clearTimeout(timeoutTimer);
    }
  };

  const handleFeedback = async (msg: ChatMessage, helpful: boolean) => {
    if (!msg.messageId || feedbackSentIds.has(msg.messageId)) return;
    const comment = window.prompt(helpful ? '感谢您的认可，如需补充请留言（可选）：' : '哪里不对？请留下纠正或建议（可选）：') || '';
    await dashboardService.sendFeedback({ messageId: msg.messageId, helpful, comment });
    setFeedbackSentIds((prev) => new Set([...prev, msg.messageId!]));
  };

  const getActionLabel = (action: ChatAction) => {
    if (action.type === 'navigate') {
      const parts: string[] = [];
      parts.push(`跳转至「${action.tabId}」`);
      if (action.blockKey) parts.push(`并定位到「${action.blockKey}」`);
      if (action.filters && Object.keys(action.filters).length > 0) parts.push('应用筛选');
      return parts.join('，');
    }
    if (action.type === 'set_filter') {
      const pairs = Object.entries(action.filters || {})
        .map(([k, v]) => `${k}=${v}`)
        .join('，');
      return `设置筛选：${pairs}`;
    }
    if (action.type === 'drill_down') {
      return `下钻到 ${action.dim}=${action.value}`;
    }
    if (action.type === 'show_daily_report') {
      return '查看经营日报';
    }
    return '执行建议';
  };

  const statusClass = (status?: 'good' | 'warning' | 'danger' | 'neutral') => {
    switch (status) {
      case 'good':
        return 'status-good';
      case 'warning':
        return 'status-warning';
      case 'danger':
        return 'status-danger';
      default:
        return 'status-neutral';
    }
  };

  const copyReportText = (report: DailyReport) => {
    const lines: string[] = [
      report.title,
      `数据周期：${report.period}`,
      `生成时间：${new Date(report.generatedAt).toLocaleString('zh-CN')}`,
      '',
      report.summary,
      '',
    ];
    for (const section of report.sections) {
      lines.push(`【${section.title}】`);
      for (const item of section.items) {
        lines.push(`- ${item.label}：${item.value}`);
      }
      lines.push('');
    }
    if (report.alerts.length > 0) {
      lines.push('【需关注异常】');
      for (const alert of report.alerts) {
        lines.push(`- ${alert.metric} | ${alert.object} = ${alert.value}`);
        lines.push(`  ${alert.advice}`);
      }
    }
    return lines.join('\n');
  };

  const handleCopyReport = (report: DailyReport) => {
    const text = copyReportText(report);
    void navigator.clipboard.writeText(text).then(() => alert('日报已复制到剪贴板'));
  };

  const renderMessageContent = (msg: ChatMessage) => {
    if (msg.status === 'thinking') {
      const step =
        msg.thinkingSteps && msg.thinkingSteps.length > 0
          ? msg.thinkingSteps[msg.thinkingSteps.length - 1]
          : msg.content || '正在思考…';
      return (
        <div className="chat-thinking-bubble">
          <span className="chat-thinking-spinner" />
          <span className="chat-thinking-text">{step}</span>
        </div>
      );
    }
    return (
      <div className="chat-text">
        {msg.status === 'typing' ? msg.displayContent || '' : msg.content}
        {msg.status === 'typing' && <span className="chat-cursor" />}
      </div>
    );
  };

  const isMessageDone = (msg: ChatMessage) => msg.status === 'done' || msg.status === 'error' || (!msg.status && msg.role === 'assistant');

  /** 获取当前角色 + 当前 tab 的推荐问题（去重） */
  const getQuickQuestions = (): string[] => {
    const roleQs = ROLE_QUICK_QUESTIONS[userRole] || ROLE_QUICK_QUESTIONS.sales_executive;
    const tabQs = TAB_QUICK_QUESTIONS[currentTab] || TAB_QUICK_QUESTIONS['综合驾驶舱'];
    const combined = [...roleQs];
    for (const q of tabQs) {
      if (!combined.includes(q)) combined.push(q);
    }
    return combined.slice(0, 6);
  };

  return (
    <div className="chat-widget">
      {open && (
        <div className="chat-panel">
          <div className="chat-header">
            <span>智能助手</span>
            <div className="chat-role-select-wrap" ref={roleDropdownRef}>
              <button
                className="chat-role-select"
                onClick={() => setRoleDropdownOpen((v) => !v)}
                title="切换角色，助手会按对应视角回答"
                aria-haspopup="listbox"
                aria-expanded={roleDropdownOpen}
              >
                {ROLE_OPTIONS.find((r) => r.value === userRole)?.label}
                <span className="chat-role-caret">{roleDropdownOpen ? '▲' : '▼'}</span>
              </button>
              {roleDropdownOpen && (
                <div className="chat-role-dropdown" role="listbox">
                  {ROLE_OPTIONS.map((r) => (
                    <button
                      key={r.value}
                      className={`chat-role-option ${r.value === userRole ? 'active' : ''}`}
                      role="option"
                      aria-selected={r.value === userRole}
                      onClick={() => {
                        setUserRole(r.value);
                        setRoleDropdownOpen(false);
                      }}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button className="chat-close" onClick={() => setOpen(false)}>
              <X size={18} />
            </button>
          </div>
          <div className="chat-messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`chat-message ${msg.role}`}>
                <div
                  className={`chat-bubble ${msg.status === 'thinking' ? 'chat-bubble-thinking' : ''} ${
                    msg.status === 'error' ? 'chat-bubble-error' : ''
                  }`}
                >
                  {renderMessageContent(msg)}
                  {(() => {
                    if (!isMessageDone(msg) || !msg.action || msg.action.type !== 'show_daily_report') return null;
                    const report = msg.action.report;
                    return (
                      <div className="chat-daily-report">
                        <div className="chat-report-header">
                          <div className="chat-report-title">{report.title}</div>
                          <div className="chat-report-meta">
                            数据周期：{report.period} · 生成于{' '}
                            {new Date(report.generatedAt).toLocaleString('zh-CN')}
                          </div>
                        </div>
                        {report.sections.map((section, si) => (
                          <div key={si} className="chat-report-section">
                            <div className="chat-report-section-title">{section.title}</div>
                            <div className="chat-report-grid">
                              {section.items.map((item, ii) => (
                                <div key={ii} className={`chat-report-item ${statusClass(item.status)}`}>
                                  <div className="chat-report-item-value">{item.value}</div>
                                  <div className="chat-report-item-label">{item.label}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                        {report.alerts.length > 0 && (
                          <div className="chat-report-section">
                            <div className="chat-report-section-title">需关注异常（{report.alerts.length}）</div>
                            <div className="chat-report-alerts">
                              {report.alerts.map((alert, ai) => (
                                <div key={ai} className="chat-report-alert">
                                  <div className="chat-report-alert-metric">{alert.metric}</div>
                                  <div className="chat-report-alert-object">{alert.object}</div>
                                  <div className="chat-report-alert-value">{alert.value}</div>
                                  <div className="chat-report-alert-advice">{alert.advice}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <button className="chat-report-copy" onClick={() => handleCopyReport(report)}>
                          复制日报到剪贴板
                        </button>
                      </div>
                    );
                  })()}
                  {(() => {
                    if (!isMessageDone(msg) || !msg.action || msg.action.type === 'show_daily_report') return null;
                    const action = msg.action;
                    return (
                      <button className="chat-action-btn" onClick={() => handleActionClick(action)}>
                        {getActionLabel(action)}
                      </button>
                    );
                  })()}
                  {isMessageDone(msg) && msg.sources && msg.sources.length > 0 && (
                    <div className="chat-sources">
                      {msg.sources.map((s, i) => (
                        <div key={i} className="chat-source-line">
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                  {isMessageDone(msg) && msg.entities && msg.entities.length > 0 && (
                    <div className="chat-entities">
                      {msg.entities.map((entity, i) => {
                        const fallbackTab =
                          msg.action && (msg.action.type === 'navigate' || msg.action.type === 'drill_down')
                            ? msg.action.tabId
                            : currentTab;
                        return (
                          <button
                            key={i}
                            className="chat-entity-chip"
                            onClick={() => handleEntityClick(entity, fallbackTab)}
                          >
                            {entity.dim}：{entity.value}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {isMessageDone(msg) && msg.suggestions && msg.suggestions.length > 0 && (
                    <div className="chat-follow-ups">
                      <div className="chat-follow-ups-title">继续追问</div>
                      <div className="chat-suggestions">
                        {msg.suggestions.map((suggestion, i) => (
                          <button
                            key={i}
                            className="chat-suggestion-chip"
                            onClick={() => handleSuggestionClick(suggestion)}
                          >
                            {suggestion.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {isMessageDone(msg) && msg.role === 'assistant' && msg.messageId && !feedbackSentIds.has(msg.messageId) && (
                    <div className="chat-feedback">
                      <button
                        className="chat-feedback-btn"
                        onClick={() => handleFeedback(msg, true)}
                        title="答案有用"
                      >
                        <ThumbsUp size={13} />
                        <span>有用</span>
                      </button>
                      <button
                        className="chat-feedback-btn"
                        onClick={() => handleFeedback(msg, false)}
                        title="答案不对"
                      >
                        <ThumbsDown size={13} />
                        <span>不对</span>
                      </button>
                    </div>
                  )}
                  {isMessageDone(msg) && msg.role === 'assistant' && msg.messageId && feedbackSentIds.has(msg.messageId) && (
                    <div className="chat-feedback-note">已反馈</div>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          {messages.length <= 1 && !loading && (
            <div className="chat-quick-questions">
              <button className="chat-daily-report-btn" onClick={handleGenerateReport}>
                <ClipboardList size={15} />
                <span>生成今日日报</span>
              </button>
              <div className="chat-quick-title">你可以这样问</div>
              <div className="chat-quick-list">
                <button
                  className="chat-quick-chip chat-quick-chip-alert"
                  onClick={() => {
                    setInput('今日有哪些异常？');
                    setTimeout(() => handleSend(), 0);
                  }}
                >
                  {alertCount > 0 ? (
                    <>
                      <AlertTriangle size={13} />
                      <span>今日 {alertCount} 项异常</span>
                    </>
                  ) : (
                    '今日有哪些异常？'
                  )}
                </button>
                {getQuickQuestions().map((q, i) => (
                  <button
                    key={i}
                    className="chat-quick-chip"
                    onClick={() => {
                      setInput(q);
                      setTimeout(() => handleSend(), 0);
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="chat-input-area">
            <input
              type="text"
              className="chat-input"
              placeholder="输入问题，例如：客户风险在哪看"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <button className="chat-send" onClick={handleSend} disabled={loading || !input.trim()}>
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
      <button
        className={`chat-float ${open ? 'active' : ''}`}
        onClick={() => setOpen(!open)}
        aria-label="打开智能助手"
      >
        {open ? <X size={22} /> : <BotMessageSquare size={22} />}
        {!open && alertCount > 0 && <span className="chat-alert-badge">{alertCount}</span>}
      </button>
    </div>
  );
}
